/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { localDate, shiftDate } from "../../shared/dates";
import { mondayOf } from "../Schedule/useRota";
import { EntryModal, type EntryDraft } from "./EntryModal";
import { PersonHours } from "./PersonHours";
import { HoursSummary } from "./Summary";
import type { HoursEntry, HoursPerson, HoursWeek } from "./types";

type Editing = {
  person: HoursPerson;
  entry: HoursEntry | null;
  defaults: { date: string; start: string; end: string; shift_id: number | null };
};

// Worked time: planned shifts next to clock entries, with what to check
// (late, left early, no-show, still clocked in, unplanned), manager
// corrections, and labour cost against sales.
// See docs/plans/workday/staff_planning.md (phase 5).
export const RotaHoursPage = defineComponent({
  name: "RotaHoursPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const route = useRoute();
    const router = useRouter();

    const fromQuery = typeof route.query.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(route.query.week) ? route.query.week : null;
    const weekStart = ref(mondayOf(fromQuery ?? localDate()));
    const range = computed(() => ({ from: weekStart.value, to: shiftDate(weekStart.value, 6) }));
    const week = ref<HoursWeek | null>(null);
    const loading = ref(false);
    const editing = ref<Editing | null>(null);
    const busy = ref(false);

    const load = async () => {
      loading.value = true;
      try {
        week.value = (await trpc.rota.hours.week.query(range.value)) as HoursWeek;
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);
    watch(weekStart, (w) => {
      router.replace({ query: { ...route.query, week: w } });
      load();
    });

    const weekLabel = computed(() => {
      const a = new Date(`${range.value.from}T12:00:00`);
      const b = new Date(`${range.value.to}T12:00:00`);
      return `${a.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${b.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
    });

    const run = async (action: () => Promise<void>) => {
      busy.value = true;
      try {
        await action();
        editing.value = null;
        await load();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        busy.value = false;
      }
    };
    const save = (draft: EntryDraft) => run(async () => void (await trpc.rota.hours.saveEntry.mutate(draft)));
    const remove = (id: number) => {
      if (!confirm(t("workday.hours.delete_confirm"))) return;
      run(async () => void (await trpc.rota.hours.deleteEntry.mutate({ id })));
    };

    const today = localDate();
    const addFor = (person: HoursPerson, defaults: Editing["defaults"] | null) => {
      const date = today >= range.value.from && today <= range.value.to ? today : range.value.from;
      editing.value = { person, entry: null, defaults: defaults ?? { date, start: "09:00", end: "17:00", shift_id: null } };
    };

    return () => (
      <div class="space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-base-300 bg-base-100 p-3">
          <div class="flex items-center gap-1">
            <button class="btn btn-ghost btn-sm btn-square" aria-label={t("workday.rota.prev_week")} onClick={() => (weekStart.value = shiftDate(weekStart.value, -7))}>
              <span class="material-symbols-outlined">chevron_left</span>
            </button>
            <div class="min-w-48 text-center font-semibold">{weekLabel.value}</div>
            <button class="btn btn-ghost btn-sm btn-square" aria-label={t("workday.rota.next_week")} onClick={() => (weekStart.value = shiftDate(weekStart.value, 7))}>
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
            <button class="btn btn-ghost btn-sm" onClick={() => (weekStart.value = mondayOf(today))}>
              {t("workday.rota.this_week")}
            </button>
          </div>
          <p class="text-sm text-base-content/60">{t("workday.hours.help")}</p>
        </div>

        {!week.value ? (
          <div class="flex justify-center py-16">
            <Loading />
          </div>
        ) : (
          <div class={["space-y-4", loading.value ? "opacity-60" : ""].join(" ")}>
            <HoursSummary week={week.value} />
            {week.value.people.length === 0 ? (
              <div class="rounded-2xl border border-base-300 bg-base-100 p-10 text-center text-base-content/60">
                <span class="material-symbols-outlined text-5xl text-base-content/30">schedule</span>
                <p class="mt-2">{t("workday.hours.empty")}</p>
              </div>
            ) : (
              <div class="space-y-2">
                {week.value.people.map((p) => (
                  <PersonHours
                    key={p.staff_id}
                    person={p}
                    timezone={week.value!.timezone}
                    canSeeWages={week.value!.can_see_wages}
                    onEdit={(entry: HoursEntry) => (editing.value = { person: p, entry, defaults: { date: entry.date, start: "", end: "", shift_id: entry.shift_id } })}
                    onAdd={(d: Editing["defaults"] | null) => addFor(p, d)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {editing.value && week.value && (
          <EntryModal
            key={`${editing.value.person.staff_id}-${editing.value.entry?.id ?? "new"}`}
            name={editing.value.person.name}
            timezone={week.value.timezone}
            entry={editing.value.entry}
            staffId={editing.value.person.staff_id}
            defaults={editing.value.defaults}
            busy={busy.value}
            onSave={save}
            onDelete={remove}
            onClose={() => (editing.value = null)}
          />
        )}
      </div>
    );
  },
});
